import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Zap, Bell, BellOff, Send } from "lucide-react";
import api from "../lib/api";
import { pushSupported, enablePush, disablePush } from "../lib/push";
import { gateName } from "../lib/gates";
import { ListenButton } from "./ListenButton";

const PLANET_GLYPH = {
  Sun: "☉", Moon: "☽", Mercury: "☿", Venus: "♀", Mars: "♂", Jupiter: "♃",
  Saturn: "♄", Uranus: "♅", Neptune: "♆", Pluto: "♇", Earth: "⊕",
  NorthNode: "☊", SouthNode: "☋",
};

export default function DailyPulse({ profileId }) {
  const [pulse, setPulse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pushOn, setPushOn] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!profileId) return;
    api.get(`/profiles/${profileId}/daily-pulse`)
      .then(({ data }) => setPulse(data))
      .catch(() => setPulse(null))
      .finally(() => setLoading(false));
    api.get(`/push/status`).then(({ data }) => setPushOn(data.enabled)).catch(() => {});
  }, [profileId]);

  const togglePush = async () => {
    setBusy(true);
    try {
      if (pushOn) { await disablePush(); setPushOn(false); toast.success("Daily insight notifications off"); }
      else { await enablePush(); setPushOn(true); toast.success("You'll get insight-driven notifications"); }
    } catch (e) { toast.error(e.message || "Could not update notifications"); } finally { setBusy(false); }
  };

  const sendNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/profiles/${profileId}/push/daily-pulse`);
      toast.success(data.sent > 0 ? `Sent to ${data.sent} device(s)` : "Enable notifications first to receive it");
    } catch (e) { toast.error("Could not send"); } finally { setBusy(false); }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="panel p-8" data-testid="daily-pulse">
      <div className="flex items-center justify-between mb-4">
        <p className="overline flex items-center gap-2"><Zap size={14} /> Daily Pulse — today's transits</p>
        <div className="flex items-center gap-2">
          {pushSupported() && (
            <button data-testid="push-toggle" onClick={togglePush} disabled={busy} title="Insight notifications"
              className={`p-2 rounded-full border transition-colors ${pushOn ? "border-primary text-primary" : "border-border text-muted-foreground hover:border-primary/50"}`}>
              {pushOn ? <Bell size={15} /> : <BellOff size={15} />}
            </button>
          )}
          <button data-testid="push-send" onClick={sendNow} disabled={busy}
            className="p-2 rounded-full border border-border text-muted-foreground hover:border-primary/50 transition-colors" title="Send me today's pulse">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
          </button>
        </div>
      </div>

      {loading ? <Loader2 className="animate-spin text-primary" size={18} /> : pulse ? (
        <>
          <p className="font-serif text-2xl leading-snug mb-2">{pulse.insight}</p>
          <ListenButton text={pulse.insight} testid="listen-daily-pulse" label="Listen to today's pulse" className="mb-5" />
          <div className="flex flex-wrap gap-2">
            {(pulse.highlights.length ? pulse.highlights : pulse.transits.slice(0, 6)).map((h) => (
              <div key={`${h.planet}-${h.gate}-${h.line}`} data-testid={`pulse-gate-${h.gate}`}
                title={`Gate ${h.gate} · ${gateName(h.gate)}${h.in_chart ? " — active in your chart" : ""}`}
                className={`px-3 py-1.5 rounded-full text-xs border flex items-center gap-1.5 ${h.in_chart ? "border-primary/60 text-primary bg-primary/10" : "border-border text-muted-foreground"}`}>
                <span className="text-sm">{PLANET_GLYPH[h.planet] || ""}</span>
                <span className="font-medium">{gateName(h.gate)}</span>
                <span className="opacity-60 font-mono">{h.gate}.{h.line}</span>
                {h.completes_channel_with?.length > 0 && <span className="text-accent" title="Completes a channel with your chart">⚡</span>}
              </div>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground mt-4">Gold gates are active in your chart today. ⚡ means today's sky completes a channel with you.</p>
        </>
      ) : <p className="text-sm text-muted-foreground">No pulse available.</p>}
    </motion.div>
  );
}
